import React, { useEffect, useMemo } from "react";
import { Flex, Typography, Pagination, Spin, Empty } from "antd";
import ImageCard from "./ImageCard";
import "./index.css";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useListCatalogQuery, usePrefetch } from "../../actions/catalogInQuery";
import { useListDevicesQuery } from "../../actions/devicesInQuery";
import { useListTrapSitesQuery } from "../../actions/trapsiteInQuery";
import { setFilters } from "../../reducers/catalogFilterSlice";
import { clearSelection } from "../../reducers/catalogSelectionSlice";
import { useDebounce } from "../../hooks/useDebounce";
import { useMinDurationLoading } from "../../hooks/useMinDurationLoading";
import {
  parseSpeciesToList,
  generateUniqueCatalogId,
} from "../../utils/helpers";

const PAGE_SIZE = 8;
const { Text } = Typography;

const Catalog: React.FC = () => {
  const dispatch = useAppDispatch();
  const filter = useAppSelector((state) => state.catalogFilter);
  const debouncedFilter = useDebounce(filter, 500);
  const currentPage = filter.page + 1;

  const filtersOnly = useMemo(() => {
    const {
      page: _p,
      hasSeededDevices: _hsd,
      hasSeededTrapsites: _hst,
      ...rest
    } = debouncedFilter;
    return JSON.stringify(rest);
  }, [debouncedFilter]);

  useEffect(() => {
    dispatch(clearSelection());
  }, [filtersOnly, dispatch]);

  const queryParams = useMemo(() => {
    const {
      species: _species,
      hasSeededDevices: _hsd,
      hasSeededTrapsites: _hst,
      ...rest
    } = debouncedFilter;
    return {
      ...rest,
      species: parseSpeciesToList(debouncedFilter.species),
      imageTypes: Object.entries(debouncedFilter.imageTypes)
        .filter(([_, checked]) => checked)
        .map(([key]) => key),
      pageSize: PAGE_SIZE,
    };
  }, [debouncedFilter]);

  const seeded =
    debouncedFilter.hasSeededDevices && debouncedFilter.hasSeededTrapsites;
  const { data, isFetching, isError, error } = useListCatalogQuery(
    queryParams,
    {
      skip: !seeded,
    },
  );
  const showLoading = useMinDurationLoading(isFetching, filter, 400);

  const { data: deviceData } = useListDevicesQuery();
  const { data: trapSiteData } = useListTrapSitesQuery();

  const deviceMap = useMemo(() => {
    if (!deviceData?.items) return {};
    return Object.fromEntries(
      deviceData.items
        .filter((d) => d.uuid)
        .map((d) => [d.uuid, d.display_name || ""]),
    );
  }, [deviceData]);

  const trapSiteMap = useMemo(() => {
    if (!trapSiteData?.items) return {};
    return Object.fromEntries(
      trapSiteData.items
        .filter((t) => t._id)
        .map((t) => [t._id, t.display_name || ""]),
    );
  }, [trapSiteData]);

  const prefetchPage = usePrefetch("listCatalog");

  useEffect(() => {
    // Prefetch next 2 pages
    if (data?.count) {
      const totalPages = Math.ceil(data.count / PAGE_SIZE);

      for (let i = 1; i <= 2; i++) {
        const nextPageIndex = debouncedFilter.page + i; // API uses 0-based page index
        if (nextPageIndex < totalPages) {
          prefetchPage({
            ...queryParams,
            page: nextPageIndex,
          });
        }
      }
    }
  }, [data?.count, queryParams, debouncedFilter.page, prefetchPage]);

  const handlePageChange = (page: number) => {
    dispatch(setFilters({ page: page - 1 }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const paginatedData = data?.items || [];
  const totalCount = data?.count || 0;

  const isFilterChangePending =
    JSON.stringify(filter) !== JSON.stringify(debouncedFilter);
  const hideContent =
    !seeded || showLoading || isFilterChangePending || isFetching;

  return (
    <div className="h-full flex flex-col">
      <div className="relative flex-1 overflow-y-auto mb-6">
        <div
          className={`${hideContent ? "invisible opacity-0" : "visible opacity-100"}`}
        >
          {isError ? (
            <Flex justify="center" align="center" className="h-full">
              <Text type="danger">
                Failed to load catalog.{" "}
                {(error as any)?.data?.message || "Please try again later."}
              </Text>
            </Flex>
          ) : paginatedData.length > 0 ? (
            <Flex wrap="wrap" gap="large" justify="start">
              {paginatedData.map((item, index) => {
                return (
                  <div
                    key={`catalog-${index}`}
                    className="w-[calc(50%-12px)] lg:w-[calc(33.33%-16px)] xl:w-[calc(25%-18px)]"
                  >
                    <ImageCard
                      {...item}
                      device_name={item.device ? deviceMap[item.device] : ""}
                      trap_site_name={
                        item.trap_site_id ? trapSiteMap[item.trap_site_id] : ""
                      }
                      catalogId={generateUniqueCatalogId(
                        item?.source,
                        item?.id,
                      )}
                      queryParams={queryParams}
                    />
                  </div>
                );
              })}
            </Flex>
          ) : (
            <Flex justify="center" align="center" className="min-h-50">
              <Empty description="No images found" />
            </Flex>
          )}
        </div>

        {hideContent && (
          <Flex
            justify="center"
            align="center"
            className="absolute inset-0 min-h-50"
          >
            <Spin size="large" />
          </Flex>
        )}
      </div>

      {!isError && (
        <Flex justify="space-between" align="center" className="mt-auto py-4">
          <Text>
            Showing {paginatedData?.length} of {totalCount} results
          </Text>
          <Pagination
            current={currentPage}
            pageSize={PAGE_SIZE}
            total={totalCount}
            onChange={handlePageChange}
            showSizeChanger={false}
            className="catalog-pagination"
          />
        </Flex>
      )}
    </div>
  );
};

export default Catalog;
